import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { Box, IconButton, Stack, Text } from '@eneco/sparky';
import { FacebookIcon, InstagramIcon, LinkedInIcon, XIcon, YouTubeIcon } from '@eneco/sparky/icons';

import type { SocialMediaMenuContent } from './social-media-menu-content.types';

/**
 * `SocialMediaMenu` block for the CMS-agnostic lane, serving **both** providers. Mirrors the legacy
 * component (`libs/sitecore/components/content/src/Footer/SocialMediaMenu.tsx`) — an optional bold title
 * above a row of round icon buttons, one per resolved link.
 *
 * Its icon-name-to-brand lookup is ported inline rather than imported: the legacy `SocialMediaIcon`
 * lives in `lib:sitecore-components`, which this lane's module boundary (`nx.json`) does not permit
 * depending on.
 *
 * See `social-media-menu-content.types.ts` for the (not yet live-confirmed) content shape and the
 * Sitecore-lane fallback (`items`) — that lane's adapter never nests content under `siteWideContent`.
 */
const SocialMediaMenu: FC = () => {
  const content = useContent<SocialMediaMenuContent>();

  const title = content.title ?? content.siteWideContent?.title;
  const items = content.siteWideContent?.items
    ? content.siteWideContent.items.flatMap((item, index) => {
        const link = linkWithUrl(item.link);
        return link
          ? [
              {
                key: item._key ?? String(index),
                link,
                icon: item.socialPlatform ?? item.icon ?? '',
                displayName: undefined,
              },
            ]
          : [];
      })
    : (content.items ?? []).flatMap((item, index) => {
        const link = linkWithUrl(item.link);
        return link
          ? [{ key: item.id ?? String(index), link, icon: item.iconName ?? '', displayName: item.displayName }]
          : [];
      });

  if (items.length === 0) return null;

  return (
    <Stack alignX={{ initial: 'start', md: 'end' }} gap="2">
      {title && (
        <Stack.Item>
          <Text color="textPrimary" weight="bold">
            {title}
          </Text>
        </Stack.Item>
      )}
      <Stack.Item>
        <Stack alignX="end" as="ul" direction="row" gap="2" wrap="true">
          {items.map(({ key, link, icon, displayName }) => (
            <Stack.Item as="li" key={key}>
              <Box backgroundColor="backgroundPrimary" borderRadius="round">
                <IconButton href={link.href} label={link.text || displayName || ''} size="large">
                  <SocialMediaIcon iconName={icon} />
                </IconButton>
              </Box>
            </Stack.Item>
          ))}
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

/** As the legacy `SocialMediaIcon.tsx`: the first brand the icon name contains, else Facebook. */
const SocialMediaIcon: FC<{ iconName: string }> = ({ iconName }) => {
  const arrSocialBrands = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube'];
  const matchedBrand = arrSocialBrands.find(brand => iconName.toLowerCase().includes(brand)) ?? 'facebook';

  switch (matchedBrand) {
    case 'youtube':
      return <YouTubeIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    case 'twitter':
      return <XIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'facebook':
    default:
      return <FacebookIcon />;
  }
};

export default SocialMediaMenu;
